/**
 * Store of Diğer › Sekreter (v0.1.28): the last sent list plus Sercan's own items, loaded when
 * the page opens; add, done and reopen call the API and read the view again. Busy keys keep a
 * double tap from sending twice ("add" for the form, the item id for a row).
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { SekreterItemInput, SekreterView } from '../api/sekreter.js';
import { useStore } from './context.js';

export interface SekreterState {
  view: SekreterView | null;
  error: string | null;
  loading: boolean;
  busy: readonly string[];
}

export class SekreterStore {
  private state: SekreterState = { view: null, error: null, loading: false, busy: [] };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): SekreterState => this.state;

  private set(patch: Partial<SekreterState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getSekreter();
        this.set({ view, error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  add(input: SekreterItemInput): Promise<string | null> {
    return this.write('add', () => this.api.addSekreterItem(input));
  }

  done(itemId: string): Promise<string | null> {
    return this.write(itemId, () => this.api.doneSekreterItem(itemId));
  }

  reopen(itemId: string): Promise<string | null> {
    return this.write(itemId, () => this.api.reopenSekreterItem(itemId));
  }

  private async write(key: string, call: () => Promise<void>): Promise<string | null> {
    if (this.state.busy.includes(key)) return null;
    this.set({ busy: [...this.state.busy, key] });
    try {
      await call();
      await this.reload();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    } finally {
      this.set({ busy: this.state.busy.filter((item) => item !== key) });
    }
  }
}

export function useSekreter(): { store: SekreterStore; state: SekreterState } {
  const store = useStore().sekreter;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
